import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart as LineIcon, TrendingUp, TrendingDown, Wallet, Loader2, Plus, Pencil, Trash2,
  Receipt, Banknote, Repeat, AlertCircle, Download, X, Building2, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  CartesianGrid, Legend,
} from 'recharts';
import * as XLSX from 'xlsx';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { RetainerTracker } from '../components/billing/RetainerTracker';
import { useToast } from '../hooks/use-toast';
import { auth, financials, getStoredUser } from '../api';

const SHEET_COL_WIDTHS = {
  summary: [{ wch: 32 }, { wch: 20 }],
  monthly: [{ wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }],
  ledger:  [{ wch: 12 }, { wch: 24 }, { wch: 40 }, { wch: 28 }, { wch: 14 }, { wch: 12 }],
  cat:     [{ wch: 32 }, { wch: 16 }, { wch: 10 }],
};

const money = (n) => 'R ' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyPrecise = (n) => 'R ' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CATEGORY_PIE_COLORS = ['#047857', '#0284c7', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#059669', '#0891b2', '#ea580c', '#64748b'];

function formatMonthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-');
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function Financials() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(12);
  const [overview, setOverview] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [categories, setCategories] = useState({ expense: [], income: [] });
  const [tab, setTab] = useState('overview');
  const [expenseDialog, setExpenseDialog] = useState(null); // {mode:'create'|'edit', data?}
  const [incomeDialog, setIncomeDialog] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    (async () => {
      try {
        const me = await auth.me().catch(() => null);
        if (me) setUser(me);
        const canSee = me?.firmRole === 'owner' || me?.isPlatformAdmin || me?.permissions?.seeFirmFinancials;
        if (!canSee) { nav('/dashboard'); return; }
        await reloadAll();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const reloadAll = async () => {
    try {
      const [ov, ex, inc, cats] = await Promise.all([
        financials.overview(range),
        financials.listExpenses(),
        financials.listIncome(),
        financials.categories(),
      ]);
      setOverview(ov);
      setExpenses(ex);
      setIncome(inc);
      setCategories(cats);
    } catch (err) {
      toast({ title: 'Could not load financials', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  // Re-fetch overview when range changes
  useEffect(() => {
    if (loading) return;
    (async () => {
      try {
        const ov = await financials.overview(range);
        setOverview(ov);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [range]);

  const kpis = useMemo(() => {
    if (!overview) return null;
    const cur = overview.currentMonth;
    const prev = overview.series[overview.series.length - 2];
    const delta = (field) => {
      if (!prev) return null;
      const p = Number(prev[field] || 0);
      const c = Number(cur[field] || 0);
      if (p === 0) return c === 0 ? 0 : 100;
      return ((c - p) / p) * 100;
    };
    return {
      mrr: overview.mrr.total,
      arr: overview.mrr.arr,
      currentRevenue: cur.revenue,
      currentExpenses: cur.expenses,
      currentProfit: cur.profit,
      revDelta: delta('revenue'),
      expDelta: delta('expenses'),
      profitDelta: delta('profit'),
      marginRange: overview.totals.margin,
      totalProfitRange: overview.totals.profit,
    };
  }, [overview]);

  const chartData = useMemo(() => {
    if (!overview) return [];
    return overview.series.map((s) => ({ ...s, label: formatMonthLabel(s.month) }));
  }, [overview]);

  const exportLedgerXLSX = () => {
    if (!overview) return;
    const firmName = user?.firm || 'Firm';
    const nowStr = new Date().toLocaleString();
    const wb = XLSX.utils.book_new();

    // --- 1. Summary sheet ---
    const summaryRows = [
      ['Firm', firmName],
      ['Report generated', nowStr],
      ['Range', `${formatMonthLabel(overview.range.from)} \u2014 ${formatMonthLabel(overview.range.to)}  (${overview.range.months} months)`],
      ['Currency', overview.currency],
      [],
      ['Monthly recurring revenue (MRR)', overview.mrr.total],
      ['Annual run-rate (ARR)', overview.mrr.arr],
      ['Active clients on retainer', overview.mrr.activeClients],
      [],
      ['Revenue (range total)', overview.totals.revenue],
      ['Expenses (range total)', overview.totals.expenses],
      ['Profit (range total)', overview.totals.profit],
      ['Profit margin', `${overview.totals.margin}%`],
      [],
      ['This month retainers', overview.currentMonth.retainers],
      ['This month other income', overview.currentMonth.otherIncome],
      ['This month revenue', overview.currentMonth.revenue],
      ['This month expenses', overview.currentMonth.expenses],
      ['This month profit', overview.currentMonth.profit],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet([['Metric', 'Value'], ...summaryRows]);
    wsSummary['!cols'] = SHEET_COL_WIDTHS.summary;
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // --- 2. Monthly P&L sheet ---
    const monthlyHeader = ['Month', 'Retainers (R)', 'Other income (R)', 'Revenue (R)', 'Expenses (R)', 'Profit (R)', 'Margin %'];
    const monthlyRows = overview.series.map((s) => {
      const margin = s.revenue > 0 ? (s.profit / s.revenue) * 100 : 0;
      return [
        formatMonthLabel(s.month),
        Number(s.retainers.toFixed(2)),
        Number(s.otherIncome.toFixed(2)),
        Number(s.revenue.toFixed(2)),
        Number(s.expenses.toFixed(2)),
        Number(s.profit.toFixed(2)),
        Number(margin.toFixed(1)),
      ];
    });
    // Totals row at the bottom
    monthlyRows.push([
      'TOTAL',
      Number(overview.series.reduce((a, s) => a + s.retainers, 0).toFixed(2)),
      Number(overview.series.reduce((a, s) => a + s.otherIncome, 0).toFixed(2)),
      Number(overview.totals.revenue.toFixed(2)),
      Number(overview.totals.expenses.toFixed(2)),
      Number(overview.totals.profit.toFixed(2)),
      Number(overview.totals.margin.toFixed(1)),
    ]);
    const wsMonthly = XLSX.utils.aoa_to_sheet([monthlyHeader, ...monthlyRows]);
    wsMonthly['!cols'] = SHEET_COL_WIDTHS.monthly;
    XLSX.utils.book_append_sheet(wb, wsMonthly, 'Monthly P&L');

    // --- 3. Expenses sheet ---
    const expenseHeader = ['Date', 'Category', 'Description', 'Vendor', 'Amount (R)', 'Recurring'];
    const expenseRows = [...expenses]
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .map((e) => [
        e.date || '',
        e.category || '',
        e.description || '',
        e.vendor || '',
        Number((Number(e.amount) || 0).toFixed(2)),
        e.recurringMonthly ? 'Yes' : 'No',
      ]);
    expenseRows.push(['', '', '', 'TOTAL', Number(expenses.reduce((a, e) => a + (Number(e.amount) || 0), 0).toFixed(2)), '']);
    const wsExpenses = XLSX.utils.aoa_to_sheet([expenseHeader, ...expenseRows]);
    wsExpenses['!cols'] = SHEET_COL_WIDTHS.ledger;
    XLSX.utils.book_append_sheet(wb, wsExpenses, 'Expenses');

    // --- 4. Income sheet ---
    const incomeHeader = ['Date', 'Category', 'Description', 'Client', 'Amount (R)', ''];
    const incomeRows = [...income]
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .map((i) => [
        i.date || '',
        i.category || '',
        i.description || '',
        i.clientName || '',
        Number((Number(i.amount) || 0).toFixed(2)),
        '',
      ]);
    incomeRows.push(['', '', '', 'TOTAL', Number(income.reduce((a, i) => a + (Number(i.amount) || 0), 0).toFixed(2)), '']);
    const wsIncome = XLSX.utils.aoa_to_sheet([incomeHeader, ...incomeRows]);
    wsIncome['!cols'] = SHEET_COL_WIDTHS.ledger;
    XLSX.utils.book_append_sheet(wb, wsIncome, 'One-off income');

    // --- 5. Expense by category ---
    const totalExpByCat = overview.expensesByCategory.reduce((a, c) => a + c.amount, 0) || 1;
    const catRows = overview.expensesByCategory.map((c) => [
      c.category,
      Number(c.amount.toFixed(2)),
      Number(((c.amount / totalExpByCat) * 100).toFixed(1)),
    ]);
    const wsCat = XLSX.utils.aoa_to_sheet([['Category', 'Amount (R)', 'Share %'], ...catRows]);
    wsCat['!cols'] = SHEET_COL_WIDTHS.cat;
    XLSX.utils.book_append_sheet(wb, wsCat, 'Expense by category');

    const safeFirm = firmName.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
    XLSX.writeFile(wb, `${safeFirm}-financials-${todayYmd()}.xlsx`);
    toast({ title: 'Excel exported', description: 'Check your downloads for the .xlsx file.' });
  };

  if (loading || !overview) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading financial health…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Financials"/>
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar user={user} firm={user?.firm}/>
        <main className="flex-1 p-4 lg:p-8 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-sky-700 text-[11px] uppercase tracking-widest font-semibold"><LineIcon size={13}/> Firm Financials</div>
              <h1 className="mt-1 font-serif text-3xl font-semibold text-stone-900">Financial health of {user?.firm || 'your firm'}</h1>
              <p className="mt-1 text-stone-600 max-w-2xl text-sm">Live P&amp;L: monthly retainers roll in automatically from the Clients page, you add one-off income and expenses here, and we chart your firm's revenue, cost and profit trends.</p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(range)} onValueChange={(v) => setRange(Number(v))}>
                <SelectTrigger className="w-[160px] h-10" data-testid="range-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Last 3 months</SelectItem>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                  <SelectItem value="24">Last 24 months</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={exportLedgerXLSX} className="h-10" data-testid="export-xlsx-btn">
                <Download size={14} className="mr-2"/> Export Excel
              </Button>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI label="Monthly recurring revenue" value={money(kpis.mrr)} sub={`${overview.mrr.activeClients} clients on retainer`} Icon={Repeat} tone="bg-emerald-50 text-emerald-800" testid="kpi-mrr"/>
            <KPI label="This month revenue" value={money(kpis.currentRevenue)} delta={kpis.revDelta} Icon={Banknote} tone="bg-sky-50 text-sky-800" testid="kpi-revenue"/>
            <KPI label="This month expenses" value={money(kpis.currentExpenses)} delta={kpis.expDelta} invertDelta Icon={Receipt} tone="bg-rose-50 text-rose-800" testid="kpi-expenses"/>
            <KPI label="This month profit" value={money(kpis.currentProfit)} delta={kpis.profitDelta} Icon={Wallet} tone={kpis.currentProfit >= 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'} testid="kpi-profit"/>
          </div>

          <div className="rounded-xl bg-white border border-stone-200 p-5 flex items-center gap-6 flex-wrap">
            <div className="min-w-[180px]">
              <div className="text-xs text-stone-500">Annual run-rate</div>
              <div className="mt-1 font-serif text-2xl font-semibold text-stone-900 tabular-nums">{money(kpis.arr)}</div>
            </div>
            <div className="min-w-[180px]">
              <div className="text-xs text-stone-500">Profit · last {range} months</div>
              <div className={`mt-1 font-serif text-2xl font-semibold tabular-nums ${kpis.totalProfitRange >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{money(kpis.totalProfitRange)}</div>
            </div>
            <div className="min-w-[180px]">
              <div className="text-xs text-stone-500">Profit margin · last {range} months</div>
              <div className={`mt-1 font-serif text-2xl font-semibold tabular-nums ${kpis.marginRange >= 20 ? 'text-emerald-700' : kpis.marginRange >= 0 ? 'text-sky-700' : 'text-rose-700'}`}>{kpis.marginRange}%</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button onClick={() => setIncomeDialog({ mode: 'create' })} className="bg-emerald-800 hover:bg-emerald-900 text-white h-10" data-testid="add-income-btn">
                <Plus size={14} className="mr-1.5"/> Log income
              </Button>
              <Button onClick={() => setExpenseDialog({ mode: 'create' })} variant="outline" className="h-10 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="add-expense-btn">
                <Plus size={14} className="mr-1.5"/> Log expense
              </Button>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-stone-200">
              <TabsTrigger value="overview" data-testid="tab-overview">Trends</TabsTrigger>
              <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses ({expenses.length})</TabsTrigger>
              <TabsTrigger value="income" data-testid="tab-income">Income ({income.length})</TabsTrigger>
              <TabsTrigger value="retainers" data-testid="tab-retainers">Retainers</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6 space-y-6">
              {/* Revenue vs expenses area chart */}
              <div className="rounded-xl bg-white border border-stone-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-semibold text-stone-900">Revenue vs expenses · last {range} months</div>
                    <div className="text-xs text-stone-500 mt-0.5">Retainers roll in automatically for every active client. One-off income + expenses come from the tabs below.</div>
                  </div>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#059669" stopOpacity={0.4}/>
                          <stop offset="100%" stopColor="#059669" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#e11d48" stopOpacity={0.3}/>
                          <stop offset="100%" stopColor="#e11d48" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4"/>
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#78716c' }}/>
                      <YAxis tick={{ fontSize: 11, fill: '#78716c' }} tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`}/>
                      <Tooltip
                        formatter={(v, name) => [money(v), name]}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e7e5e4' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }}/>
                      <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#059669" strokeWidth={2} fill="url(#rev)"/>
                      <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#e11d48" strokeWidth={2} fill="url(#exp)"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profit bar chart */}
                <div className="lg:col-span-2 rounded-xl bg-white border border-stone-200 p-5">
                  <div className="font-semibold text-stone-900">Monthly profit</div>
                  <div className="text-xs text-stone-500 mt-0.5 mb-4">Green bars = profit, red bars = loss.</div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4"/>
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#78716c' }}/>
                        <YAxis tick={{ fontSize: 11, fill: '#78716c' }} tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`}/>
                        <Tooltip
                          formatter={(v) => [money(v), 'Profit']}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e7e5e4' }}
                        />
                        <Bar dataKey="profit" radius={[6, 6, 0, 0]}>
                          {chartData.map((entry, i) => (
                            <Cell key={i} fill={entry.profit >= 0 ? '#059669' : '#e11d48'}/>
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Expense category pie */}
                <div className="rounded-xl bg-white border border-stone-200 p-5">
                  <div className="font-semibold text-stone-900">Expense mix</div>
                  <div className="text-xs text-stone-500 mt-0.5 mb-2">Last {range} months.</div>
                  {overview.expensesByCategory.length === 0 ? (
                    <div className="h-56 grid place-items-center text-sm text-stone-400">No expenses yet</div>
                  ) : (
                    <>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={overview.expensesByCategory}
                              dataKey="amount"
                              nameKey="category"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={2}
                            >
                              {overview.expensesByCategory.map((entry, i) => (
                                <Cell key={i} fill={CATEGORY_PIE_COLORS[i % CATEGORY_PIE_COLORS.length]}/>
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 12 }}/>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <ul className="mt-2 space-y-1 text-xs">
                        {overview.expensesByCategory.slice(0, 6).map((e, i) => (
                          <li key={e.category} className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_PIE_COLORS[i % CATEGORY_PIE_COLORS.length] }}/>
                            <span className="flex-1 truncate text-stone-700">{e.category}</span>
                            <span className="tabular-nums font-medium text-stone-900">{money(e.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>

              {/* Breakdown table */}
              <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
                <div className="p-5 border-b border-stone-100 font-semibold text-stone-900">Monthly breakdown</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-5 py-3 font-medium">Month</th>
                        <th className="text-right px-5 py-3 font-medium">Retainers</th>
                        <th className="text-right px-5 py-3 font-medium">Other income</th>
                        <th className="text-right px-5 py-3 font-medium">Revenue</th>
                        <th className="text-right px-5 py-3 font-medium">Expenses</th>
                        <th className="text-right px-5 py-3 font-medium">Profit</th>
                        <th className="text-right px-5 py-3 font-medium">Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {[...overview.series].reverse().map((s) => {
                        const margin = s.revenue > 0 ? (s.profit / s.revenue) * 100 : 0;
                        return (
                          <tr key={s.month} className="hover:bg-stone-50" data-testid={`row-${s.month}`}>
                            <td className="px-5 py-3 font-medium text-stone-900">{formatMonthLabel(s.month)}</td>
                            <td className="px-5 py-3 text-right tabular-nums text-stone-700">{money(s.retainers)}</td>
                            <td className="px-5 py-3 text-right tabular-nums text-stone-700">{money(s.otherIncome)}</td>
                            <td className="px-5 py-3 text-right tabular-nums font-medium text-emerald-700">{money(s.revenue)}</td>
                            <td className="px-5 py-3 text-right tabular-nums text-rose-700">{money(s.expenses)}</td>
                            <td className={`px-5 py-3 text-right tabular-nums font-semibold ${s.profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{money(s.profit)}</td>
                            <td className={`px-5 py-3 text-right tabular-nums text-xs ${margin >= 20 ? 'text-emerald-700' : margin >= 0 ? 'text-sky-700' : 'text-rose-700'}`}>{margin.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="expenses" className="mt-6">
              <LedgerTable
                items={expenses}
                emptyIcon={Receipt}
                emptyLabel="No expenses yet. Log your first one to start tracking firm costs."
                onEdit={(e) => setExpenseDialog({ mode: 'edit', data: e })}
                onDelete={async (e) => {
                  if (!window.confirm(`Delete expense "${e.description || e.category}" for ${money(e.amount)}?`)) return;
                  try {
                    await financials.deleteExpense(e.id);
                    toast({ title: 'Expense deleted' });
                    await reloadAll();
                  } catch (err) {
                    toast({ title: 'Delete failed', description: err?.response?.data?.detail, variant: 'destructive' });
                  }
                }}
                kind="expense"
              />
            </TabsContent>

            <TabsContent value="retainers" className="mt-6"><RetainerTracker toast={toast}/></TabsContent>

            <TabsContent value="income" className="mt-6">
              <LedgerTable
                items={income}
                emptyIcon={Banknote}
                emptyLabel="No one-off income logged yet. Monthly retainers from Clients already flow into revenue — this tab is for extras."
                onEdit={(i) => setIncomeDialog({ mode: 'edit', data: i })}
                onDelete={async (i) => {
                  if (!window.confirm(`Delete income "${i.description || i.category}" for ${money(i.amount)}?`)) return;
                  try {
                    await financials.deleteIncome(i.id);
                    toast({ title: 'Income entry deleted' });
                    await reloadAll();
                  } catch (err) {
                    toast({ title: 'Delete failed', description: err?.response?.data?.detail, variant: 'destructive' });
                  }
                }}
                kind="income"
              />
            </TabsContent>
          </Tabs>
        </main>
      </div>

      <ExpenseDialog
        state={expenseDialog}
        onClose={() => setExpenseDialog(null)}
        categories={categories.expense}
        onSaved={reloadAll}
      />
      <IncomeDialog
        state={incomeDialog}
        onClose={() => setIncomeDialog(null)}
        categories={categories.income}
        onSaved={reloadAll}
      />
    </div>
  );
}

function KPI({ label, value, sub, delta, invertDelta, Icon, tone, testid }) {
  const hasDelta = delta !== null && delta !== undefined && isFinite(delta);
  const positiveIsGood = !invertDelta;
  const isGood = hasDelta && ((positiveIsGood && delta >= 0) || (!positiveIsGood && delta <= 0));
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-5" data-testid={testid}>
      <div className={`inline-flex h-9 w-9 rounded-lg items-center justify-center ${tone}`}><Icon size={16}/></div>
      <div className="mt-3 font-serif text-2xl font-semibold text-stone-900 tabular-nums">{value}</div>
      <div className="text-xs text-stone-500 mt-0.5">{label}</div>
      {hasDelta && (
        <div className={`mt-1.5 inline-flex items-center gap-1 text-xs font-medium ${isGood ? 'text-emerald-700' : 'text-rose-700'}`}>
          {isGood ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
          {Math.abs(delta).toFixed(1)}% vs last month
        </div>
      )}
      {!hasDelta && sub && <div className="mt-1.5 text-xs text-stone-500">{sub}</div>}
    </div>
  );
}

function LedgerTable({ items, emptyIcon: EmptyIcon, emptyLabel, onEdit, onDelete, kind }) {
  if (items.length === 0) {
    return (
      <div className="rounded-xl bg-white border border-stone-200 p-12 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-stone-100 grid place-items-center text-stone-500 mb-3"><EmptyIcon size={20}/></div>
        <div className="text-sm text-stone-500 max-w-md mx-auto">{emptyLabel}</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Date</th>
              <th className="text-left px-5 py-3 font-medium">Category</th>
              <th className="text-left px-5 py-3 font-medium">Description</th>
              <th className="text-left px-5 py-3 font-medium">{kind === 'expense' ? 'Vendor' : 'Client'}</th>
              <th className="text-right px-5 py-3 font-medium">Amount</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-stone-50" data-testid={`${kind}-row-${it.id}`}>
                <td className="px-5 py-3 text-stone-700 whitespace-nowrap">{it.date}</td>
                <td className="px-5 py-3">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-stone-50 border-stone-200 text-stone-700">
                    {it.category}
                    {it.recurringMonthly && <Repeat size={10} title="Recurring monthly"/>}
                  </span>
                </td>
                <td className="px-5 py-3 text-stone-700 max-w-sm truncate">{it.description || <span className="text-stone-400">—</span>}</td>
                <td className="px-5 py-3 text-stone-700">{kind === 'expense' ? (it.vendor || <span className="text-stone-400">—</span>) : (it.clientName || <span className="text-stone-400">—</span>)}</td>
                <td className={`px-5 py-3 text-right tabular-nums font-semibold ${kind === 'expense' ? 'text-rose-700' : 'text-emerald-700'}`}>{moneyPrecise(it.amount)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => onEdit(it)} className="h-7 w-7 grid place-items-center text-stone-500 hover:text-stone-900 rounded" title="Edit" data-testid={`${kind}-edit-${it.id}`}><Pencil size={14}/></button>
                    <button onClick={() => onDelete(it)} className="h-7 w-7 grid place-items-center text-stone-400 hover:text-red-600 rounded" title="Delete" data-testid={`${kind}-delete-${it.id}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpenseDialog({ state, onClose, categories, onSaved }) {
  const { toast } = useToast();
  const open = !!state;
  const mode = state?.mode;
  const [form, setForm] = useState({ date: todayYmd(), category: categories[0] || 'Other', amount: '', description: '', vendor: '', recurringMonthly: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && state.data) {
        setForm({
          date: state.data.date || todayYmd(),
          category: state.data.category || (categories[0] || 'Other'),
          amount: state.data.amount || '',
          description: state.data.description || '',
          vendor: state.data.vendor || '',
          recurringMonthly: !!state.data.recurringMonthly,
        });
      } else {
        setForm({ date: todayYmd(), category: categories[0] || 'Other', amount: '', description: '', vendor: '', recurringMonthly: false });
      }
    }
  }, [open, mode, state?.data, categories]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!(amt > 0)) {
      toast({ title: 'Amount is required', description: 'Enter an amount greater than zero.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, amount: amt };
      if (mode === 'edit') await financials.updateExpense(state.data.id, payload);
      else await financials.createExpense(payload);
      toast({ title: mode === 'edit' ? 'Expense updated' : 'Expense logged' });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt size={16} className="text-rose-700"/> {mode === 'edit' ? 'Edit expense' : 'Log an expense'}</DialogTitle>
          <DialogDescription>Office rent, salaries, software, SARS payments — anything your firm spent.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-date">Date</Label>
              <Input id="exp-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5"/>
            </div>
            <div>
              <Label htmlFor="exp-amount">Amount (ZAR)</Label>
              <Input id="exp-amount" type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="mt-1.5" data-testid="expense-amount"/>
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5" data-testid="expense-category"><SelectValue/></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="exp-vendor">Vendor <span className="text-stone-400 font-normal">(optional)</span></Label>
            <Input id="exp-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Who did you pay?" className="mt-1.5"/>
          </div>
          <div>
            <Label htmlFor="exp-desc">Description</Label>
            <Textarea id="exp-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What was this for?" className="mt-1.5"/>
          </div>
          <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg bg-sky-50/50 border border-sky-100 hover:bg-sky-50">
            <Checkbox checked={form.recurringMonthly} onCheckedChange={(v) => setForm({ ...form, recurringMonthly: !!v })} data-testid="expense-recurring"/>
            <div className="min-w-0">
              <div className="text-sm font-medium text-sky-900">Recurring monthly expense</div>
              <div className="text-[11px] text-sky-800/70 mt-0.5">We'll auto-include this amount every month from this date forward in the P&amp;L (eg rent, salaries, Xero subscription).</div>
            </div>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="expense-save-btn">
              {busy ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Plus size={14} className="mr-1.5"/>}
              {mode === 'edit' ? 'Save changes' : 'Log expense'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IncomeDialog({ state, onClose, categories, onSaved }) {
  const { toast } = useToast();
  const open = !!state;
  const mode = state?.mode;
  const [form, setForm] = useState({ date: todayYmd(), category: categories[0] || 'Other', amount: '', description: '', clientName: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && state.data) {
        setForm({
          date: state.data.date || todayYmd(),
          category: state.data.category || (categories[0] || 'Other'),
          amount: state.data.amount || '',
          description: state.data.description || '',
          clientName: state.data.clientName || '',
        });
      } else {
        setForm({ date: todayYmd(), category: categories[0] || 'Other', amount: '', description: '', clientName: '' });
      }
    }
  }, [open, mode, state?.data, categories]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!(amt > 0)) {
      toast({ title: 'Amount is required', description: 'Enter an amount greater than zero.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, amount: amt };
      if (mode === 'edit') await financials.updateIncome(state.data.id, payload);
      else await financials.createIncome(payload);
      toast({ title: mode === 'edit' ? 'Income updated' : 'Income logged' });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="income-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Banknote size={16} className="text-emerald-700"/> {mode === 'edit' ? 'Edit income' : 'Log one-off income'}</DialogTitle>
          <DialogDescription>For revenue outside the monthly retainer — tax-return fees, setup charges, consulting, audits, etc.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="inc-date">Date</Label>
              <Input id="inc-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5"/>
            </div>
            <div>
              <Label htmlFor="inc-amount">Amount (ZAR)</Label>
              <Input id="inc-amount" type="number" min="0" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="mt-1.5" data-testid="income-amount"/>
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5" data-testid="income-category"><SelectValue/></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="inc-client">Client <span className="text-stone-400 font-normal">(optional)</span></Label>
            <Input id="inc-client" value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} placeholder="Who paid you?" className="mt-1.5"/>
          </div>
          <div>
            <Label htmlFor="inc-desc">Description</Label>
            <Textarea id="inc-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What was this for?" className="mt-1.5"/>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="income-save-btn">
              {busy ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Plus size={14} className="mr-1.5"/>}
              {mode === 'edit' ? 'Save changes' : 'Log income'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
